import type { ThemeToken, ThemeModeTokens, Typography } from '../store/types';
import { lightTokens } from '../store/types';

const REF = /^\{([\w.]+)\}$/;

function lookup(mode: ThemeModeTokens, path: string): string | undefined {
  const [group, key] = path.split('.');
  const g = (mode as unknown as Record<string, Record<string, unknown>>)[group];
  const v = g?.[key];
  return typeof v === 'string' ? v : undefined;
}

/** Resolve {group.key} references in a mode's components against that same mode. */
export function resolveModeRefs(mode: ThemeModeTokens): ThemeModeTokens {
  if (!mode.components) return mode;
  const components: NonNullable<ThemeModeTokens['components']> = {};
  for (const [name, props] of Object.entries(mode.components)) {
    // Component tokens are agent-authored and untyped at runtime. The type
    // promises a CSS-props object, but an LLM often emits a class string
    // (e.g. "rounded-lg border p-4"). Object.entries on a string yields
    // char-index pairs ({0:'r',1:'o',…}) that later crash `style=` in
    // production React — preserve non-object tokens verbatim instead.
    if (!props || typeof props !== 'object' || Array.isArray(props)) {
      components[name] = props as never;
      continue;
    }
    components[name] = {};
    for (const [k, val] of Object.entries(props)) {
      const m = typeof val === 'string' ? val.match(REF) : null;
      components[name][k] = m ? (lookup(mode, m[1]) ?? val) : val;
    }
  }
  return { ...mode, components };
}

/** Back-compat shim: resolve refs treating a whole ThemeToken as its light mode.
 *  Kept as a back-compat shim (used by the StyleguideFrameView test). */
export function resolveTokenRefs(theme: ThemeToken): ThemeToken {
  const resolved = resolveModeRefs(lightTokens(theme));
  return { ...theme, components: resolved.components };
}

// Variable naming aligns with Tailwind v4 @theme namespaces so utilities generate.
function colorVar(k: string) { return `--color-${k}`; }
function radiusVar(k: string) { return `--radius-${k}`; }
function spacingVar(k: string) { return `--spacing-${k}`; }
function fontVar(k: string) { return `--font-${k}`; }
function textVar(k: string) { return `--text-${k}`; }

// Tailwind v4 reads font-size companions as --text-<k>--line-height / --font-weight / --letter-spacing,
// so a single text-<k> utility applies size + line-height + weight + tracking together.
function typographyDecls(prefix: string, t: Typography): string[] {
  const out: string[] = [];
  if (t.fontFamily) out.push(`${fontVar(prefix)}: ${t.fontFamily};`);
  if (t.fontSize) out.push(`${textVar(prefix)}: ${t.fontSize};`);
  if (t.lineHeight != null && t.lineHeight !== '') out.push(`${textVar(prefix)}--line-height: ${t.lineHeight};`);
  if (t.fontWeight != null && t.fontWeight !== '') out.push(`${textVar(prefix)}--font-weight: ${t.fontWeight};`);
  if (t.letterSpacing) out.push(`${textVar(prefix)}--letter-spacing: ${t.letterSpacing};`);
  return out;
}

/** Fill in any optional dark groups so a partial dark set is a full ThemeModeTokens. */
function coerceDark(d: Partial<ThemeModeTokens>): ThemeModeTokens {
  return {
    colors: d.colors ?? {},
    typography: d.typography ?? {},
    rounded: d.rounded ?? {},
    spacing: d.spacing ?? {},
    components: d.components,
    shadow: d.shadow,
    motion: d.motion,
  };
}

/** Every mode (light + dark) of every theme — for key unions + default lookup. */
function allModes(themes: ThemeToken[]): ThemeModeTokens[] {
  const out: ThemeModeTokens[] = [];
  for (const t of themes) {
    out.push(lightTokens(t));
    if (t.dark) out.push(coerceDark(t.dark));
  }
  return out;
}

function firstTypographyField(modes: ThemeModeTokens[], key: string, field: keyof Typography): string | number | undefined {
  for (const m of modes) {
    const v = (m.typography ?? {})[key]?.[field];
    if (v != null && v !== '') return v;
  }
  return undefined;
}

function collectKeys(modes: ThemeModeTokens[]) {
  const colors = new Set<string>(), rounded = new Set<string>(),
    spacing = new Set<string>(), fonts = new Set<string>(), texts = new Set<string>();
  for (const m of modes) {
    Object.keys(m.colors ?? {}).forEach((k) => colors.add(k));
    Object.keys(m.rounded ?? {}).forEach((k) => rounded.add(k));
    Object.keys(m.spacing ?? {}).forEach((k) => spacing.add(k));
    for (const [k, ty] of Object.entries(m.typography ?? {})) {
      if (ty.fontFamily) fonts.add(k);
      if (ty.fontSize) texts.add(k);
    }
  }
  return { colors, rounded, spacing, fonts, texts };
}

/** CSS var declaration lines (indented) for one resolved mode. */
function modeVarLines(m: ThemeModeTokens): string[] {
  const lines: string[] = [];
  for (const [k, v] of Object.entries(m.colors ?? {})) lines.push(`  ${colorVar(k)}: ${v};`);
  for (const [k, v] of Object.entries(m.rounded ?? {})) lines.push(`  ${radiusVar(k)}: ${v};`);
  for (const [k, v] of Object.entries(m.spacing ?? {})) lines.push(`  ${spacingVar(k)}: ${String(v)};`);
  for (const [k, ty] of Object.entries(m.typography ?? {})) lines.push(...typographyDecls(k, ty).map((d) => `  ${d}`));
  return lines;
}

export function compileThemes(
  themes: ThemeToken[],
): { themeBlock: string; scopeBlocks: string } {
  const modes = allModes(themes);
  const keys = collectKeys(modes);
  const first = modes[0];
  const decl: string[] = [];
  keys.colors.forEach((k) => decl.push(`  ${colorVar(k)}: ${first?.colors?.[k] ?? 'inherit'};`));
  keys.rounded.forEach((k) => decl.push(`  ${radiusVar(k)}: ${first?.rounded?.[k] ?? '0'};`));
  keys.spacing.forEach((k) => decl.push(`  ${spacingVar(k)}: ${String(first?.spacing?.[k] ?? '0')};`));
  keys.fonts.forEach((k) => decl.push(`  ${fontVar(k)}: ${firstTypographyField(modes, k, 'fontFamily') ?? 'inherit'};`));
  keys.texts.forEach((k) => {
    decl.push(`  ${textVar(k)}: ${firstTypographyField(modes, k, 'fontSize') ?? '1rem'};`);
    const lh = firstTypographyField(modes, k, 'lineHeight');
    if (lh != null) decl.push(`  ${textVar(k)}--line-height: ${lh};`);
    const fw = firstTypographyField(modes, k, 'fontWeight');
    if (fw != null) decl.push(`  ${textVar(k)}--font-weight: ${fw};`);
    const ls = firstTypographyField(modes, k, 'letterSpacing');
    if (ls != null) decl.push(`  ${textVar(k)}--letter-spacing: ${ls};`);
  });
  // `@theme reference` registers the token names so Tailwind generates the
  // utilities (bg-primary, rounded-card, text-display, …) WITHOUT emitting the
  // values to :root. Emitting them globally leaked the first theme's radius/
  // color tokens onto the app chrome (e.g. the ActionChip's `rounded-full`
  // resolving a themed --radius → stretched ellipse). Real values come from the
  // per-frame `[data-theme]` scope blocks instead (see applyTheme).
  const themeBlock = `@theme reference {\n${decl.join('\n')}\n}`;

  const scopeBlocks = themes.map((t) => {
    const blocks: string[] = [];
    const light = modeVarLines(resolveModeRefs(lightTokens(t)));
    blocks.push(`[data-theme="${t.id}"] {\n${light.join('\n')}\n}`);
    if (t.dark) {
      const dark = modeVarLines(resolveModeRefs(coerceDark(t.dark)));
      const body = dark.join('\n');
      const mediaBody = dark.map((l) => '  ' + l).join('\n');
      blocks.push(`.dark [data-theme="${t.id}"] {\n${body}\n}`);
      blocks.push(`@media (prefers-color-scheme: dark) {\n  :root:not(.light):not(.dark) [data-theme="${t.id}"] {\n${mediaBody}\n  }\n}`);
    }
    return blocks.join('\n\n');
  }).join('\n\n');

  // Bind each role's font-family onto its text-<role> utility too (the standard
  // --text-* namespace can't carry family); --font-<role> resolves per scope/mode.
  const fontBinds = [...keys.texts]
    .filter((k) => keys.fonts.has(k))
    .map((k) => `.text-${k} { font-family: var(${fontVar(k)}); }`)
    .join('\n');
  const scopeBlocksWithBinds = fontBinds ? `${scopeBlocks}\n\n${fontBinds}` : scopeBlocks;

  return { themeBlock, scopeBlocks: scopeBlocksWithBinds };
}
